import { useEffect, useMemo, useState } from 'react'
import { tournamentApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'

/**
 * Polymorphic credential form for external tee-sheet providers.
 *
 * Renders provider-specific fields, a "Test Connection" button that calls
 * POST /api/golf-bookings/test-connection, and emits the validated
 * credentials object back up via onChange + onTestResult.
 *
 * Supported providers:
 *   - teeon-classic  : applicationKey + accessToken + courseCode (+ optional nineCode)
 *   - teeon-gn       : facilityToken + courseCode (+ optional divisionCode)
 *   - ttc            : teesheet (ttc|chronogolf) + crsid dropdown + rateCode dropdown
 *                      The dropdowns are populated from
 *                        GET /api/golf-bookings/providers/ttc/courses
 *                        GET /api/golf-bookings/providers/ttc/rates?crsid=&teesheet=
 *
 * Returns null for providers that need no credentials (e.g. 'integolf').
 */
export default function ProviderCredentialsForm({ provider, initialCredentials, onChange, onTestResult, onCourseMetadata }) {
  const { t } = useLanguage()
  const [creds, setCreds] = useState(initialCredentials || {})
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)

  // TTC-specific catalog state
  const [ttcCourses, setTtcCourses] = useState([])
  const [ttcCoursesLoading, setTtcCoursesLoading] = useState(false)
  const [ttcCoursesError, setTtcCoursesError] = useState('')
  const [ttcRates, setTtcRates] = useState([])
  const [ttcRatesLoading, setTtcRatesLoading] = useState(false)
  const [ttcRatesError, setTtcRatesError] = useState('')
  // Typeahead state for the TTC course picker
  const [ttcSearch, setTtcSearch] = useState('')
  const [ttcDropdownOpen, setTtcDropdownOpen] = useState(false)

  // tee-time.com (Libeo) organization catalog + typeahead state
  const [ttcomOrgs, setTtcomOrgs] = useState([])
  const [ttcomOrgsLoading, setTtcomOrgsLoading] = useState(false)
  const [ttcomOrgsError, setTtcomOrgsError] = useState('')
  const [ttcomOrgSearch, setTtcomOrgSearch] = useState('')
  const [ttcomOrgDropdownOpen, setTtcomOrgDropdownOpen] = useState(false)

  // ChronoGolf organization + player-type catalogs
  const [cgOrgs, setCgOrgs] = useState([])
  const [cgOrgsLoading, setCgOrgsLoading] = useState(false)
  const [cgOrgsError, setCgOrgsError] = useState('')
  const [cgPlayerTypes, setCgPlayerTypes] = useState([])
  const [cgPlayerTypesLoading, setCgPlayerTypesLoading] = useState(false)
  const [cgPlayerTypesError, setCgPlayerTypesError] = useState('')

  // Reset state when the provider key changes (e.g. user flips between cards)
  useEffect(() => {
    setCreds(initialCredentials || {})
    setTestResult(null)
    setTtcCourses([])
    setTtcRates([])
    setTtcCoursesError('')
    setTtcRatesError('')
    setTtcomOrgs([])
    setTtcomOrgsError('')
    setCgOrgs([])
    setCgOrgsError('')
    setCgPlayerTypes([])
    setCgPlayerTypesError('')
  }, [provider]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load ChronoGolf organization catalog when provider switches to chronogolf
  useEffect(() => {
    if (provider !== 'chronogolf') return
    let cancelled = false
    setCgOrgsLoading(true)
    setCgOrgsError('')
    tournamentApi.get('/golf-bookings/providers/chronogolf/organizations')
      .then(({ data }) => {
        if (cancelled) return
        setCgOrgs(Array.isArray(data?.organizations) ? data.organizations : [])
      })
      .catch(err => {
        if (cancelled) return
        setCgOrgsError(err.response?.data?.message || err.message || t('sp_chronogolf_catalog_failed', 'Failed to load ChronoGolf catalog (OAuth credentials may not be configured yet)'))
      })
      .finally(() => !cancelled && setCgOrgsLoading(false))
    return () => { cancelled = true }
  }, [provider])

  // Load ChronoGolf player types when the selected organization changes
  useEffect(() => {
    if (provider !== 'chronogolf' || !creds.organizationId) { setCgPlayerTypes([]); return }
    let cancelled = false
    setCgPlayerTypesLoading(true)
    setCgPlayerTypesError('')
    tournamentApi.get('/golf-bookings/providers/chronogolf/player-types', {
      params: { organizationId: creds.organizationId },
    })
      .then(({ data }) => {
        if (cancelled) return
        setCgPlayerTypes(Array.isArray(data?.playerTypes) ? data.playerTypes : [])
      })
      .catch(err => {
        if (cancelled) return
        setCgPlayerTypesError(err.response?.data?.message || err.message || t('sp_player_types_failed', 'Failed to load player types'))
      })
      .finally(() => !cancelled && setCgPlayerTypesLoading(false))
    return () => { cancelled = true }
  }, [provider, creds.organizationId])

  // Load tee-time.com organization catalog for the teetime-com provider
  useEffect(() => {
    if (provider !== 'teetime-com') return
    let cancelled = false
    setTtcomOrgsLoading(true)
    setTtcomOrgsError('')
    tournamentApi.get('/golf-bookings/providers/teetime-com/organizations')
      .then(({ data }) => {
        if (cancelled) return
        setTtcomOrgs(Array.isArray(data?.organizations) ? data.organizations : [])
      })
      .catch(err => {
        if (cancelled) return
        setTtcomOrgsError(err.response?.data?.message || err.message || t('sp_teetime_catalog_failed', 'Failed to load tee-time.com catalog (API key may not be configured yet)'))
      })
      .finally(() => !cancelled && setTtcomOrgsLoading(false))
    return () => { cancelled = true }
  }, [provider])

  // Bubble changes up to the parent
  useEffect(() => {
    if (onChange) onChange(creds)
  }, [creds]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load the TTC course catalog once for the ttc provider
  useEffect(() => {
    if (provider !== 'ttc') return
    let cancelled = false
    setTtcCoursesLoading(true)
    setTtcCoursesError('')
    tournamentApi.get('/golf-bookings/providers/ttc/courses')
      .then(({ data }) => {
        if (cancelled) return
        setTtcCourses(Array.isArray(data?.courses) ? data.courses : [])
      })
      .catch(err => {
        if (cancelled) return
        setTtcCoursesError(err.response?.data?.message || err.message || t('sp_ttc_catalog_failed', 'Failed to load TTC course catalog'))
      })
      .finally(() => !cancelled && setTtcCoursesLoading(false))
    return () => { cancelled = true }
  }, [provider])

  // Load TTC rates whenever the picked crsid or teesheet changes
  useEffect(() => {
    if (provider !== 'ttc') return
    if (!creds.crsid || !creds.teesheet) {
      setTtcRates([])
      return
    }
    let cancelled = false
    setTtcRatesLoading(true)
    setTtcRatesError('')
    tournamentApi.get('/golf-bookings/providers/ttc/rates', {
      params: { crsid: creds.crsid, teesheet: creds.teesheet },
    })
      .then(({ data }) => {
        if (cancelled) return
        setTtcRates(Array.isArray(data?.rates) ? data.rates : [])
      })
      .catch(err => {
        if (cancelled) return
        setTtcRatesError(err.response?.data?.message || err.message || t('sp_ttc_rates_failed', 'Failed to load rate catalog'))
      })
      .finally(() => !cancelled && setTtcRatesLoading(false))
    return () => { cancelled = true }
  }, [provider, creds.crsid, creds.teesheet])

  function updateField(key, value) {
    setCreds(c => ({ ...c, [key]: value }))
    setTestResult(null)
  }

  async function handleTestConnection() {
    setTesting(true)
    setTestResult(null)
    try {
      const { data } = await tournamentApi.post('/golf-bookings/test-connection', {
        provider,
        credentials: creds,
      })
      setTestResult(data)
      if (onTestResult) onTestResult(data)
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('sp_test_connection_failed', 'Test connection failed')
      const result = { ok: false, message: msg }
      setTestResult(result)
      if (onTestResult) onTestResult(result)
    } finally {
      setTesting(false)
    }
  }

  // InteGolf needs no external credentials
  if (provider === 'integolf' || !provider) return null

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>{t('sp_provider_credentials_h', 'Provider credentials')}</h3>

      {provider === 'teeon-classic' && (
        <>
          <Field label={t('sp_application_key', 'Application Key')} required>
            <input
              type="text"
              className="input"
              value={creds.applicationKey || ''}
              onChange={e => updateField('applicationKey', e.target.value)}
              placeholder="Tee-On Application Key"
            />
          </Field>
          <Field label={t('sp_access_token', 'Access Token')} required>
            <input
              type="password"
              className="input"
              value={creds.accessToken || ''}
              onChange={e => updateField('accessToken', e.target.value)}
              placeholder="Tee-On Access Token"
            />
          </Field>
          <Field label={t('sp_course_code', 'Course Code')} required>
            <input
              type="text"
              className="input"
              value={creds.courseCode || ''}
              onChange={e => updateField('courseCode', e.target.value)}
              placeholder="e.g. LINGAN"
            />
          </Field>
          <Field label={t('sp_nine_code_optional', 'Nine Code (optional)')} hint={t('sp_nine_code_hint', 'Leave blank if your course has only one nine.')}>
            <input
              type="text"
              className="input"
              value={creds.nineCode || ''}
              onChange={e => updateField('nineCode', e.target.value)}
              placeholder={t('sp_optional_placeholder', 'Optional')}
            />
          </Field>
          <NoSandboxWarning />
        </>
      )}

      {provider === 'teeon-gn' && (
        <>
          <Field label={t('sp_facility_token', 'Facility Token')} required hint={t('sp_facility_token_hint', 'Issued by Tee-On for your specific course.')}>
            <input
              type="password"
              className="input"
              value={creds.facilityToken || ''}
              onChange={e => updateField('facilityToken', e.target.value)}
              placeholder="Tee-On Golf North Facility Token"
            />
          </Field>
          <Field label={t('sp_course_code', 'Course Code')} required>
            <input
              type="text"
              className="input"
              value={creds.courseCode || ''}
              onChange={e => updateField('courseCode', e.target.value)}
              placeholder="e.g. LINGAN"
            />
          </Field>
          <Field label={t('sp_division_code_optional', 'Division Code (optional)')} hint={t('sp_division_code_hint', 'Leave blank unless you book a specific division.')}>
            <input
              type="text"
              className="input"
              value={creds.divisionCode || ''}
              onChange={e => updateField('divisionCode', e.target.value)}
              placeholder={t('sp_optional_placeholder', 'Optional')}
            />
          </Field>
        </>
      )}

      {provider === 'ttc' && (
        <>
          <Field label={t('sp_ttc_course', 'TTC Course')} required hint={t('sp_ttc_course_hint', 'Type to filter — picked from the catalog visible to InteGolf\'s shared TTC account. The backend (TTC native vs ChronoGolf) is auto-detected from the catalog row.')}>
            {ttcCoursesLoading && <div style={mutedStyle}>{t('sp_loading_catalog', 'Loading catalog...')}</div>}
            {ttcCoursesError && <div style={errorStyle}>{ttcCoursesError}</div>}
            {!ttcCoursesLoading && !ttcCoursesError && (() => {
              // Filter to BOOKABLE courses only — TTC's catalog returns
              // teesheet='manual' for courses that exist but can't be booked
              // through the API (TTC just lists them). Hide those entirely
              // since we can't actually transact against them.
              const bookable = ttcCourses.filter(c => c.teesheet && c.teesheet !== 'manual')

              // Typeahead: filter by name OR code, case-insensitive
              const selected = bookable.find(c => c.courseCode === creds.crsid)
              const inputValue = ttcDropdownOpen
                ? ttcSearch
                : (selected ? `${selected.courseName} (${selected.courseCode})` : ttcSearch)
              const q = ttcSearch.trim().toLowerCase()
              const filtered = q
                ? bookable.filter(c =>
                    c.courseName?.toLowerCase().includes(q) ||
                    c.courseCode?.toLowerCase().includes(q))
                : bookable
              return (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="input"
                    value={inputValue}
                    onChange={e => {
                      setTtcSearch(e.target.value)
                      setTtcDropdownOpen(true)
                      // If they cleared the field after picking, clear creds
                      if (e.target.value === '' && creds.crsid) {
                        setCreds(c => ({ ...c, crsid: '', courseName: '', rateCode: '', rateName: '' }))
                        setTestResult(null)
                      }
                    }}
                    onFocus={() => { setTtcDropdownOpen(true); setTtcSearch('') }}
                    onBlur={() => { setTimeout(() => setTtcDropdownOpen(false), 150) }}
                    placeholder={t('sp_select_course_option', '-- Type to filter courses --')}
                    autoComplete="off"
                  />
                  {ttcDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0,
                      background: '#fff', border: '1px solid #e2e8f0',
                      borderTop: 'none', borderRadius: '0 0 6px 6px',
                      maxHeight: 280, overflowY: 'auto', zIndex: 100,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}>
                      {filtered.length === 0
                        ? <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>{t('sp_no_courses_match_q', 'No courses match')} "{ttcSearch}".</div>
                        : filtered.slice(0, 50).map(c => {
                            const isSelected = c.courseCode === creds.crsid
                            return (
                              <div
                                key={c.courseCode}
                                onMouseDown={e => {
                                  // onMouseDown so the input's onBlur doesn't fire first
                                  e.preventDefault()
                                  setCreds(prev => ({
                                    ...prev,
                                    crsid: c.courseCode,
                                    courseName: c.courseName || '',
                                    teesheet: c.teesheet || prev.teesheet,
                                    rateCode: '',
                                    rateName: '',
                                  }))
                                  // Emit course metadata up so the parent can
                                  // auto-prefill course basics (name, city,
                                  // state, lat/lng if available, etc.)
                                  if (onCourseMetadata) onCourseMetadata({
                                    name: c.courseName || '',
                                    city: c.city || '',
                                    state: c.state || '',
                                    country: c.country || '',
                                    postalCode: c.postalCode || '',
                                    latitude: c.latitude,
                                    longitude: c.longitude,
                                    phone: c.phone || '',
                                    email: c.email || '',
                                    website: c.website || '',
                                  })
                                  setTestResult(null)
                                  setTtcSearch('')
                                  setTtcDropdownOpen(false)
                                }}
                                style={{
                                  padding: '8px 12px', cursor: 'pointer',
                                  background: isSelected ? '#ecfdf5' : '#fff',
                                  borderLeft: isSelected ? '3px solid #00d19f' : '3px solid transparent',
                                  fontSize: 13,
                                }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc' }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#fff' }}
                              >
                                <div style={{ color: '#0f172a', fontWeight: isSelected ? 600 : 400 }}>{c.courseName}</div>
                                <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                                  {c.courseCode} {c.teesheet !== 'ttc' ? `· ${c.teesheet}` : ''}
                                </div>
                              </div>
                            )
                          })}
                      {filtered.length > 50 && (
                        <div style={{ padding: 8, color: '#94a3b8', fontSize: 12, textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>
                          {t('sp_n_more_keep_typing', '{n} more — keep typing to narrow', { n: filtered.length - 50 })}
                        </div>
                      )}
                    </div>
                  )}
                  {selected && !ttcDropdownOpen && (
                    <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                      Selected: <code>{selected.courseCode}</code> · backend: <code>{selected.teesheet}</code>
                    </div>
                  )}
                </div>
              )
            })()}
          </Field>

          <Field label={t('sp_rate_code', 'Rate Code')} required hint={t('sp_rate_code_hint', 'Which rate this course will sell through Stay & Play. Likely-public rates are ⭐ marked + grouped at the top.')}>
            {!creds.crsid && <div style={mutedStyle}>{t('sp_pick_course_first', 'Pick a course first.')}</div>}
            {creds.crsid && ttcRatesLoading && <div style={mutedStyle}>{t('sp_loading_rates', 'Loading rates...')}</div>}
            {creds.crsid && ttcRatesError && <div style={errorStyle}>{ttcRatesError}</div>}
            {creds.crsid && !ttcRatesLoading && !ttcRatesError && (() => {
              // Heuristic: flag rates whose name looks publicly-bookable so
              // they float to the top with a ⭐. Caveats:
              //  - Member-bound rates ("Member Guest", "Junior", "Staff", etc.)
              //    are excluded EVEN IF a positive keyword matches.
              //  - Pricing structure varies wildly per course; the user still
              //    has to pick. The star is a hint, not auto-pick.
              const POSITIVE = /(?:^|\b)(visitor|public|rack|non[- ]?member|standard|green[ -]?fee|walk[- ]?(?:on|up)|day pass|open rate|general|pay[- ]?and[- ]?play)\b/i
              const NEGATIVE = /\b(member|members|member's|junior|senior|league|staff|comp|complimentary|certificate|practice|introductory|industry|reciprocal|tour operator|wedding|corporate|society|suspended|sandbagger|passport|player passport|condo|wedding|chicks with sticks|ladies invitational|seaview|lifetime|intermediate|twilight|inverary|le portage|lingan|ms night|mens night|nova scotia)\b/i
              function isLikelyPublic(name) {
                if (!name) return false
                if (NEGATIVE.test(name)) return false
                return POSITIVE.test(name)
              }
              const sorted = [...ttcRates].sort((a, b) => {
                const aL = isLikelyPublic(a.rateName) ? 0 : 1
                const bL = isLikelyPublic(b.rateName) ? 0 : 1
                if (aL !== bL) return aL - bL
                return (a.rateName || '').localeCompare(b.rateName || '')
              })
              const publicCount = sorted.filter(r => isLikelyPublic(r.rateName)).length
              return (
                <>
                  <select
                    className="input"
                    value={creds.rateCode || ''}
                    onChange={e => {
                      const rateCode = e.target.value
                      const match = ttcRates.find(r => r.rateCode === rateCode)
                      setCreds(c => ({
                        ...c,
                        rateCode,
                        rateName: match?.rateName || '',
                      }))
                      setTestResult(null)
                    }}
                  >
                    <option value="">{t('sp_select_rate_option', '-- Select a rate --')}</option>
                    {publicCount > 0 && (
                      <optgroup label={t('sp_rates_likely_public_group', '⭐ Likely public / non-member rates')}>
                        {sorted.filter(r => isLikelyPublic(r.rateName)).map(r => (
                          <option key={r.rateCode} value={r.rateCode}>
                            ⭐ {r.rateName}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label={t('sp_rates_all_other_group', 'All other rates')}>
                      {sorted.filter(r => !isLikelyPublic(r.rateName)).map(r => (
                        <option key={r.rateCode} value={r.rateCode}>
                          {r.rateName}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  {publicCount > 0 && !creds.rateCode && (
                    <div style={{ marginTop: 6, padding: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4, fontSize: 12, color: '#065f46' }}>
                      {t('sp_rates_publicly_bookable_notice', '⭐ {count} rate(s) look publicly bookable — confirm with the course before going live, since names vary.', { count: publicCount })}
                    </div>
                  )}
                </>
              )
            })()}
          </Field>

          <NoSandboxWarning />
        </>
      )}

      {provider === 'chronogolf' && (
        <>
          <Field label={t('sp_chronogolf_org', 'ChronoGolf Organization')} required hint={t('sp_chronogolf_org_hint', 'Your course\'s org in the Lightspeed catalog. Loaded live from the Partner API.')}>
            {cgOrgsLoading && <div style={mutedStyle}>{t('sp_loading_catalog', 'Loading catalog...')}</div>}
            {cgOrgsError && <div style={errorStyle}>{cgOrgsError}</div>}
            {!cgOrgsLoading && !cgOrgsError && (
              <select
                className="input"
                value={creds.organizationId || ''}
                onChange={e => {
                  const id = e.target.value
                  const match = cgOrgs.find(o => o.id === id)
                  setCreds(c => ({
                    ...c,
                    organizationId: id,
                    organizationName: match?.name || '',
                    // Reset dependent field when org changes
                    defaultPlayerTypeId: '',
                    playerTypeName: '',
                  }))
                  setTestResult(null)
                }}
              >
                <option value="">{t('sp_select_org_option', '-- Select an organization --')}</option>
                {cgOrgs.map(o => (
                  <option key={o.id} value={o.id} disabled={!o.onlineBookingEnabled}>
                    {o.name}{o.onlineBookingEnabled ? '' : t('sp_online_booking_disabled', ' (online booking disabled)')}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label={t('sp_default_player_type', 'Default Player Type')} required hint={t('sp_default_player_type_hint', 'Which rate tier to book customers under (e.g. \'Public\', \'Partner Rate\').')}>
            {!creds.organizationId && <div style={mutedStyle}>{t('sp_pick_org_first', 'Pick an organization first.')}</div>}
            {creds.organizationId && cgPlayerTypesLoading && <div style={mutedStyle}>{t('sp_loading_player_types', 'Loading player types...')}</div>}
            {creds.organizationId && cgPlayerTypesError && <div style={errorStyle}>{cgPlayerTypesError}</div>}
            {creds.organizationId && !cgPlayerTypesLoading && !cgPlayerTypesError && (
              <select
                className="input"
                value={creds.defaultPlayerTypeId || ''}
                onChange={e => {
                  const id = e.target.value
                  const match = cgPlayerTypes.find(p => p.id === id)
                  setCreds(c => ({ ...c, defaultPlayerTypeId: id, playerTypeName: match?.name || '' }))
                  setTestResult(null)
                }}
              >
                <option value="">{t('sp_select_player_type_option', '-- Select a player type --')}</option>
                {cgPlayerTypes.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.defaultRole ? ` · ${p.defaultRole}` : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div style={{
            marginTop: 4, padding: '10px 12px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 8, fontSize: 12, color: '#1e40af',
          }}>
            <strong>{t('sp_oauth_shared_strong', 'OAuth credentials are shared.')}</strong> {t('sp_oauth_shared_blurb', 'The Lightspeed Partner OAuth app (client_id, refresh_token, tour_operator_id) is configured once at the platform level. Per-course, you just pick the organization and player type.')}
          </div>
          <NoSandboxWarning />
        </>
      )}

      {provider === 'teetime-com' && (
        <>
          <Field label={t('sp_organization_optional', 'Organization (optional)')} hint={t('sp_org_optional_hint', 'Leave blank to search ALL tee-time.com orgs within 50 km. Pick one to restrict results to that organization only.')}>
            {ttcomOrgsLoading && <div style={mutedStyle}>{t('sp_loading_teetime_catalog', 'Loading tee-time.com catalog...')}</div>}
            {ttcomOrgsError && <div style={errorStyle}>{ttcomOrgsError}</div>}
            {!ttcomOrgsLoading && !ttcomOrgsError && (() => {
              // Typeahead combobox: a single input that shows the picked org's
              // formatted name when closed and acts as a filter when open.
              // Mirrors the TTC course picker pattern above.
              const formatLabel = (o) => `${o.name}${o.cityName ? ` (${o.cityName}${o.province ? ', ' + o.province : ''})` : ''}`
              const sorted = [...ttcomOrgs].sort((a, b) =>
                String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
              )
              const selected = creds.organizationId
                ? ttcomOrgs.find(o => Number(o.id) === Number(creds.organizationId))
                : null
              const inputValue = ttcomOrgDropdownOpen
                ? ttcomOrgSearch
                : (selected ? formatLabel(selected) : ttcomOrgSearch)
              const q = ttcomOrgSearch.trim().toLowerCase()
              const filtered = q
                ? sorted.filter(o => formatLabel(o).toLowerCase().includes(q))
                : sorted
              return (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="input"
                    value={inputValue}
                    placeholder={t('sp_any_org_within_radius', '— Any organization within radius —')}
                    onChange={e => {
                      setTtcomOrgSearch(e.target.value)
                      setTtcomOrgDropdownOpen(true)
                      // Cleared the field after picking — clear the selection.
                      if (e.target.value === '' && creds.organizationId) {
                        setCreds(c => ({ ...c, organizationId: null, organizationName: '' }))
                        setTestResult(null)
                      }
                    }}
                    onFocus={() => { setTtcomOrgDropdownOpen(true); setTtcomOrgSearch('') }}
                    onBlur={() => { setTimeout(() => setTtcomOrgDropdownOpen(false), 150) }}
                    autoComplete="off"
                  />
                  {ttcomOrgDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0,
                      background: '#fff', border: '1px solid #e2e8f0',
                      borderTop: 'none', borderRadius: '0 0 6px 6px',
                      maxHeight: 280, overflowY: 'auto', zIndex: 100,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}>
                      {/* Always offer the "any org within radius" reset row
                          at the top so the customer can wipe their pick. */}
                      <div
                        onMouseDown={e => {
                          e.preventDefault()
                          setCreds(c => ({ ...c, organizationId: null, organizationName: '' }))
                          setTestResult(null)
                          setTtcomOrgSearch('')
                          setTtcomOrgDropdownOpen(false)
                        }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer',
                          fontStyle: 'italic', color: '#64748b',
                          borderBottom: '1px solid #f1f5f9',
                          background: !creds.organizationId ? '#f0fdf4' : 'transparent',
                        }}
                      >
                        {t('sp_any_org_within_radius', '— Any organization within radius —')}
                      </div>
                      {filtered.length === 0
                        ? <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>
                            {t('sp_teetime_org_filter_no_match_q', 'No organizations match')} "{ttcomOrgSearch}".
                          </div>
                        : filtered.slice(0, 100).map(o => {
                            const isSelected = Number(o.id) === Number(creds.organizationId)
                            return (
                              <div
                                key={o.id}
                                onMouseDown={e => {
                                  e.preventDefault()
                                  setCreds(c => ({
                                    ...c,
                                    organizationId: Number(o.id),
                                    organizationName: o.name || '',
                                  }))
                                  setTestResult(null)
                                  setTtcomOrgSearch('')
                                  setTtcomOrgDropdownOpen(false)
                                }}
                                style={{
                                  padding: '8px 12px', cursor: 'pointer',
                                  background: isSelected ? '#f0fdf4' : 'transparent',
                                  fontWeight: isSelected ? 600 : 400,
                                }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc' }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                              >
                                {formatLabel(o)}
                              </div>
                            )
                          })}
                      {filtered.length > 100 && (
                        <div style={{ padding: '6px 12px', fontSize: 11, color: '#94a3b8', borderTop: '1px solid #f1f5f9' }}>
                          {t('sp_teetime_org_first_100', 'First 100 shown')} ({filtered.length} {t('sp_teetime_org_total', 'total')}). {t('sp_teetime_org_keep_typing', 'Keep typing to narrow.')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}
          </Field>
          <div style={{
            marginTop: 4, padding: '10px 12px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 8, fontSize: 12, color: '#1e40af',
          }}>
            <strong>{t('sp_search_only_strong', 'Search-only for now.')}</strong> {t('sp_search_only_blurb', 'Booking via tee-time.com requires VIPGolf partner credentials we are still working on. Customers will see availability but bookings are handled out-of-band.')}
          </div>
          <NoSandboxWarning />
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={testing}
          className="btn btn-outline-dark"
          style={{ padding: '10px 20px' }}
        >
          {testing ? t('sp_testing', 'Testing...') : t('sp_test_connection', 'Test connection')}
        </button>
        {testResult && (
          <div style={testResult.ok ? resultOkStyle : resultErrStyle}>
            <span aria-hidden="true" style={{ marginRight: 6 }}>{testResult.ok ? t('sp_ok_caps', 'OK') : t('sp_fail_caps', 'FAIL')}</span>
            {testResult.message}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Shared tiny components ───────────────────────────────────────────

function Field({ label, hint, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#dc2626' }} aria-label="required">*</span>}
      </label>
      {children}
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  )
}

function RadioPill({ checked, onChange, label }) {
  return (
    <label style={{
      padding: '8px 14px',
      borderRadius: 20,
      border: '1px solid ' + (checked ? 'var(--brand-500)' : 'var(--card-border)'),
      background: checked ? 'var(--brand-50)' : '#fff',
      color: checked ? 'var(--brand-700)' : 'var(--text-muted)',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    }}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        style={{ margin: 0 }}
      />
      {label}
    </label>
  )
}

function NoSandboxWarning() {
  const { t } = useLanguage()
  return (
    <div style={{
      marginTop: 8,
      padding: '10px 12px',
      background: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: 8,
      fontSize: 12,
      color: '#92400e',
    }}>
      <strong>{t('sp_no_sandbox_strong', 'No sandbox environment.')}</strong> {t('sp_no_sandbox_blurb', 'Test Connection will hit the live production API. Any real booking created here will be visible to the course.')}
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────

const containerStyle = {
  marginTop: 24,
  padding: 24,
  background: 'var(--brand-50)',
  border: '1px solid var(--card-border)',
  borderRadius: 12,
}
const titleStyle = {
  margin: '0 0 16px',
  fontSize: 16,
  color: 'var(--secondary-950)',
}
const hintStyle = {
  marginTop: 4,
  fontSize: 11,
  color: 'var(--text-muted)',
}
const mutedStyle = {
  fontSize: 12,
  color: 'var(--text-muted)',
  padding: '8px 0',
}
const errorStyle = {
  fontSize: 12,
  color: '#dc2626',
  padding: '8px 0',
}
const resultOkStyle = {
  fontSize: 12,
  padding: '6px 12px',
  borderRadius: 6,
  background: '#dcfce7',
  color: '#166534',
  border: '1px solid #86efac',
}
const resultErrStyle = {
  fontSize: 12,
  padding: '6px 12px',
  borderRadius: 6,
  background: '#fef2f2',
  color: '#991b1b',
  border: '1px solid #fca5a5',
}
